<script lang="ts">
  import { getAppConfig } from '../../../common/config/app-config';
  import { getStr, globalCurrencyTag, isDirectionRTL } from '../../../common/util';
  import {
    PriceSplitProviderType,
    instalmentOrder,
  } from '../../../modules/common/lazy-components/driver/common-mapper';
  import { getTamaraPreCheckURL } from '../services/pdp-data-services';
  import PdpSectionAccordionCard from './PDPSectionAccordionCard.svelte';

  export let data: any;

  const rtl = isDirectionRTL();
  const {
    encodedContextPath,
    unbxdSearchConfig: {
      tasheelInfo: { tasheelImageUrl },
    },
  } = getAppConfig();
  const tabbyImageLogoUrl = getStr('tabbyImageLogoUrl');
  const tamaraImageLogoUrl = getStr('tamaraImageLogoUrl');
  const floossImageLogoUrl = getStr('floossImageLogoUrl');
  const qpayImageLogoUrl = getStr('qpayImageLogoUrl');

  $: visible = getComponentVisibility(data, instalmentOrder);
  $: bankMuscatLogo = data?.easyPaymentDetails?.iconUrl;
  const startingFrom = getStartingFromSummary();

  function getComponentVisibility(data: any, instalmentOrder: PriceSplitProviderType[]) {
    return instalmentOrder.some(p => p in data && !!data[p]);
  }
  function getProviderImage(provider: PriceSplitProviderType) {
    switch (provider) {
      case 'baseeta':
        return tasheelImageUrl;
      case 'tamara':
        return tamaraImageLogoUrl;
      case 'tabby':
        return tabbyImageLogoUrl;
      case 'easyPaymentDetails':
        return bankMuscatLogo;
      case 'flooss':
        return floossImageLogoUrl;
      case 'qpay':
        return qpayImageLogoUrl;
      default:
        return '';
    }
  }
  function getProviderMessage(provider: PriceSplitProviderType, providerData: any) {
    switch (provider) {
      case 'baseeta':
        return getStr('extra.pdp.instalment.baseeta.new.message', [
          providerData.tasheelInstallmentMonths,
          globalCurrencyTag,
          providerData.priceValue,
        ]);
      case 'tabby':
        return getStr('extra.pdp.instalment.tabby.message', [
          providerData.installments,
          globalCurrencyTag,
          providerData.installmentAmount.formattedValue,
        ]);
      case 'tamara':
        return providerData.extraTamaraMsg;
      case 'easyPaymentDetails':
        return getStr('extra.easypayment.installment.pdp.text', [
          globalCurrencyTag,
          providerData.minimumThreshold.formattedValue,
        ]);
      case 'flooss':
        return getStr('extra.pdp.cart.instalment.flooss.new.message', [providerData.installments]);
      case 'qpay':
        return getStr('extra.pdp.instalment.qpay.message', [
          providerData.installments,
          globalCurrencyTag,
          providerData.installmentAmount.formattedValue,
        ]);

      default:
        return '';
    }
  }
  function getProviderLinkDetails(provider: PriceSplitProviderType, providerData: any) {
    const linkDetails = {
      href: '',
      text: '',
      isTamaraBNPLPlus: false,
    };
    switch (provider) {
      case 'baseeta':
        linkDetails.href = `${encodedContextPath}${providerData.tasheelContentUrl}`;
        linkDetails.text = getStr('extra.pdp.instalment.baseeta.link.text');
        break;
      case 'tamara':
        linkDetails.href = `${encodedContextPath}${providerData.learnMoreLink}`;
        linkDetails.text = providerData.isTamaraBNPL
          ? getStr('extra.pdp.instalment.tamara.link.text')
          : getStr('extra.pdp.instalment.tamara.precheck.link.text');
        linkDetails.isTamaraBNPLPlus = !providerData.isTamaraBNPL;
        break;
      case 'tabby':
        linkDetails.href = `${encodedContextPath}${providerData.learnMoreLink}`;
        linkDetails.text = getStr('extra.pdp.instalment.tamara.link.text');
        break;
      case 'easyPaymentDetails':
        linkDetails.href = `${providerData.linkUrl}`;
        linkDetails.text = getStr('extra.easypayment.installment.pdp.redirect.linktext');
        break;
      case 'flooss':
        linkDetails.href = `${encodedContextPath}${providerData.floossLink}`;
        linkDetails.text = getStr('extra.flooss.learnmore.message');
        break;
      case 'qpay':
        linkDetails.href = `${encodedContextPath}${providerData.learnMoreLink}`;
        linkDetails.text = getStr('extra.qpay.learnmore.message');
        break;
    }
    return linkDetails;
  }
  function gtmInstalmentClick(provider: PriceSplitProviderType) {
    var productDetails = window['ACC'].config.productDetails;
    var productData = [
      {
        item_id: productDetails?.code,
        item_name: productDetails?.nameEn?.toLowerCase()?.replace(/['"]/g, ''),
        item_brand: productDetails?.brandNameEn?.toLowerCase(),
        item_category: productDetails?.pageCategory?.toLowerCase(),
        model_num: productDetails.modelNumber,
      },
    ];
    window['ACC'].extragtm.click.pdpInstalmentsClick(provider + '_learnmore_clicks', productData);
  }

  function getStartingAmount(provider: PriceSplitProviderType, providerData: any) {
    switch (provider) {
      case 'baseeta':
        return providerData?.priceValue;
      case 'tabby':
      case 'tamara':
        return providerData?.installmentAmount?.formattedValue;
      case 'easyPaymentDetails':
        return providerData?.minimumThreshold?.formattedValue;
      default:
        return null;
    }
  }

  function getStartingFromSummary() {
    const preferredProviderOrder: PriceSplitProviderType[] = [
      'baseeta',
      'tamara',
      'tabby',
      'easyPaymentDetails',
      'flooss',
    ];
    for (const provider of preferredProviderOrder) {
      if (!data?.[provider]) continue;
      const amount = getStartingAmount(provider, data[provider]);
      if (amount) {
        return `${getStr('extra.pdp.starting.from.months.message', [globalCurrencyTag, `<strong>${amount}</strong>`])}`;
      }
    }
    return '';
  }

  function getProviderHeading(provider: PriceSplitProviderType) {
    switch (provider) {
      case 'baseeta':
        return getStr('extra.pdp.instalment.baseeta.header');
      case 'tabby':
        return getStr('extra.pdp.instalment.tabby.header');
      case 'tamara':
        return getStr('extra.pdp.instalment.tamara.header');
      case 'qpay':
        return '';
      default:
        return '';
    }
  }

  async function openTamaraPreCheck() {
    const productCode = window['ACC']?.config?.productDetails?.code;
    if (!productCode) return;

    const response = await getTamaraPreCheckURL(productCode);

    if (response?.redirectUrl) {
      window.location.assign(response.redirectUrl);
    }
  }
</script>

{#if visible}
  <PdpSectionAccordionCard titleKey="extra.pdp.installment" icon="icon-card" initiallyExpanded={false}>
    <span slot="header-right" class="starting-from-text">{@html startingFrom}</span>
    <section class="instalment-provider-list">
      {#each instalmentOrder as providerName}
        {@const providerHeading = getProviderHeading(providerName)}
        {#if data[providerName]}
          {@const linkDetails = getProviderLinkDetails(providerName, data[providerName])}
          <section class="instalment-provider provider-{providerName}">
            <div class="instalment-provider-left">
              <img class:rtl class="logo" src={getProviderImage(providerName)} alt={providerName} />
            </div>
            <div class="instalment-provider-right">
              {#if providerHeading}
                <div class="heading">
                  {@html providerHeading}
                </div>
              {/if}
              <div class="message">
                {@html getProviderMessage(providerName, data[providerName])}
              </div>
              {#if !!linkDetails.isTamaraBNPLPlus}
                <!-- svelte-ignore a11y-invalid-attribute -->
                <a href="javascript:void(0)" class="learn-more-link" on:click={() => openTamaraPreCheck()}
                  >{linkDetails.text}</a
                >
              {:else if !!linkDetails.href}
                <a href={linkDetails.href} class="learn-more-link" on:click={() => gtmInstalmentClick(providerName)}
                  >{linkDetails.text}</a
                >
              {/if}
            </div>
          </section>
        {/if}
      {/each}
    </section>
  </PdpSectionAccordionCard>
{/if}

<style lang="scss">
  @import '../../common/styles/colors.new.scss';

  :global(.pdp-section-card) {
    .starting-from-text {
      color: $neutral-dark-02;
      font-size: 14px;
      line-height: 16px;
      font-weight: 400;
      white-space: nowrap;
      :global(strong) {
        font-weight: 700;
        font-size: 20px;
        line-height: 18px;
      }
    }
  }

  .instalment-provider-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    .instalment-provider {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      background-color: $primary-white;
      padding: 8px;
      border-radius: 8px;
      .logo {
        max-height: 40px;
        max-width: 60px;
        object-position: center left;
        object-fit: contain;
        flex-shrink: 0;
        @include desktop-max-width {
          max-width: 56px;
        }
      }
      .message {
        flex-grow: 1;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
        color: $neutral-dark-02;
        @include desktop-max-width {
          font-size: 12px;
          line-height: 16px;
        }

        :global(strong) {
          font-weight: 700;
        }
      }
      .heading {
        flex-grow: 1;
        font-size: 14px;
        font-weight: 700;
        line-height: 16px;
        color: $neutral-dark-02;
        @include desktop-max-width {
          font-size: 12px;
        }
      }
      .learn-more-link {
        color: $primary-blue;
        font-size: 12px;
        line-height: 20px;
        font-weight: 500;
        white-space: nowrap;
        @include desktop-max-width {
          font-size: 11px;
          line-height: 14px;
        }
      }
      &.provider-flooss {
        .logo {
          max-height: initial;
          max-width: 75px;
        }
      }
    }
  }
</style>
